import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from "rxjs";
import { SharedStoreService } from "./shared-store.service";
import { ProgramChoiceService } from "../shared/services/api/program-choice.service";
import {
  AppLoadError,
  degreePrepProgram,
  EEliOption,
  englishLanguageInstitute,
  EnglishProficiency,
  IApplicantInfo,
  IEnglishProficiency,
  IProgramChoiceApp,
  ProgramChoiceApp,
  senecaLanguageCentre,
} from "../shared/models";
import { ApplicantInfoStoreService } from "./applicant-info-store.service";
import { EnglishProficiencyStoreService } from "./english-proficiency-store.service";
import { HttpErrorResponse } from "@angular/common/http";
import { campusNameConverter } from "../utils";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class ProgramChoiceStoreService {
  private readonly _programChoice =
    new BehaviorSubject<IProgramChoiceApp | null>(null);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<AppLoadError | null>(null);
  private readonly _saving = new BehaviorSubject<boolean>(false); // Track saving state

  programChoice$ = this._programChoice.asObservable();
  loading$ = this._loading.asObservable();
  error$ = this._error.asObservable();
  saving$ = this._saving.asObservable(); // Expose saving state as observable
  destroyed$: any;
  programs = [];

  get programChoice() {
    return this._programChoice.getValue();
  }

  constructor(
    private service: ProgramChoiceService,
    private applicantInfoStore: ApplicantInfoStoreService,
    private englishProficiencyStore: EnglishProficiencyStoreService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  selectProgramChoice(): Observable<IProgramChoiceApp> {
    return this.programChoice$.pipe(map((programChoice) => programChoice));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  selectSaving(): Observable<boolean> {
    // Provide a method to check saving state
    return this.saving$.pipe(map((saving) => saving));
  }

  selectProgramChoiceReview() {
    return this.programChoice$.pipe(
      map((programChoiceApps) => {
        if (programChoiceApps) {
          return programChoiceApps.choices.map(
            (
              { startDate, campus, program, description, choiceNumber },
              index
            ) => ({
              index: index + 1,
              startDate: this.startDateToTermConverter(new Date(startDate)),
              program,
              description,
              campus,
              choiceNumber,
            })
          );
        }
        return null;
      })
    );
  }

  /**
   * @POST
   */
  submitProgramChoices(programChoices: IProgramChoiceApp) {
    this._loading.next(true);
    this._saving.next(true); // Indicate that saving is in progress

    return this.service.postProgramChoices(programChoices).pipe(
      map(() => {
        programChoices.choices = this.formatChoices(programChoices.choices);
        this._programChoice.next(programChoices);
        console.log(programChoices);
        this._saving.next(false); // Indicate that saving is complete

        this.gtmService.pushTag({
          event: "international_application_progress",
          step_number: "4",
          step_name: "program-choice",
        });

        this.sharedStore.triggerNavigationAfterSubmit("PROGRAM_CHOICES");

        return programChoices;
      }),

      catchError((err: HttpErrorResponse) => {
        this.handleError(err);
        this._saving.next(false); // Indicate that saving failed
        return of(null);
      }),
      tap(() => this._loading.next(false))
    );
  }

  /**
   * Loads the program choices depending if the application is for ELI or is a default program
   * @GET
   **/
  loadProgramChoices() {
    return combineLatest([
      this.applicantInfoStore.selectApplicantInfo(),
      this.service.getProgramChoices(),
    ]).pipe(
      map(([applicantInfo, programChoice]) => {
        this.getEliOption().subscribe((englishProf) => {
          if (Object.keys(programChoice).length === 0){
            programChoice = this.createDefaultProgramChoice(applicantInfo);
          }
          const updatedChoices = this.checkForEliAndDpp(
            programChoice,
            englishProf.eliOption
          );
          this._programChoice.next(updatedChoices);

          return updatedChoices;
        });
      }),
      catchError((err: any) => {
        this.handleError(err);
        return of(null);
      })
    );
  }

  /**
   * @GET
   */
  private getEliOption() {
    if (this.applicantInfoStore.applicantInfo.appId){
      return this.englishProficiencyStore
      .loadEnglishProficiency()
      .pipe(map((englishProf) => englishProf));
    }
  }

  private createDefaultProgramChoice(applicantInfo): ProgramChoiceApp {
    const programChoice = new ProgramChoiceApp(applicantInfo.appId);
    programChoice.applicationId = applicantInfo.appId;

    return programChoice;
  }

  private formatChoices(choices: any[]): any[] {
    return choices.map((choice) => {
      if (choice.startDate) {
        choice.startDate = choice.startDate.toString();
      }
      choice.campus = campusNameConverter(choice.campus);
      return choice;
    });
  }

  // private formatDate(stringDate: string): Date {

  //   const separator = "-";
  //   const date = stringDate.split(separator);
  //   if (date.length === 2) {
  //     const year = Number(date[0]);
  //     const month = Number(date[1]) - 1;
  //     return new Date(year, month);
  //   }
  //   return null;
  // }

  private checkForEliAndDpp(
    programChoice: any,
    eliOption: string
  ): IProgramChoiceApp {
    switch (eliOption) {
      case EEliOption.ATTEND_ELI:
        this.addProgramChoiceIfNotExists(
          programChoice,
          englishLanguageInstitute
        );
        break;
      case EEliOption.ATTEND_SLC:
        this.addProgramChoiceIfNotExists(programChoice, senecaLanguageCentre);
        break;
      case EEliOption.ATTEND_DPP:
        this.addProgramChoiceIfNotExists(programChoice, degreePrepProgram);
        break;
      default:
        this.removeInvalidProgramChoices(programChoice);
    }
  
    return programChoice;
  }

  private addProgramChoiceIfNotExists(
    programChoice: ProgramChoiceApp,
    programInfo: any
  ): void {
    if (
      Object.keys(programChoice).length === 0 ||
      !programChoice.choices[0] ||
      programChoice.choices[0].program !== programInfo.programCode ||
      programChoice.choices[0].description !== programInfo.programDesc
    ) {
      const startDate = programChoice?.choices[0]?.startDate || null;

      programChoice.choices[0] = {
        startDate: startDate,
        campus: programInfo.campus,
        program: programInfo.programCode,
        description: programInfo.programDesc,
        acadCareer: programInfo.acadCareer,
        acadPlan: programInfo.acadPlan,
        bundleEligible: "N",
        choiceNumber: 0,
      };
    }
  }

  private removeInvalidProgramChoices(programChoice: ProgramChoiceApp): void {
    if (
      programChoice.choices[0] &&
      (programChoice.choices[0].program ===
        englishLanguageInstitute.programCode ||
        programChoice.choices[0].program === senecaLanguageCentre.programCode ||
        programChoice.choices[0].program === degreePrepProgram.programCode)
    ) {
      programChoice.choices = [];
    }
  }

  private handleError(err: any): void {
    const error = err instanceof HttpErrorResponse ? err.error : err;
    const displayError = new AppLoadError(error.id, error.message);
    this._error.next(displayError);
    this._loading.next(false);
    this._saving.next(false); // Handle saving error
  }

  private startDateToTermConverter = (date: Date) => {
    let term: string;
    try {
      const month = date.getUTCMonth();

      switch (true) {
        case month >= 0 && month < 4:
          term = "Winter";
          break;
        case month >= 4 && month < 8:
          term = "Summer";
          break;
        case month >= 8 && month < 12:
          term = "Fall";
          break;
        default:
          term = "Unidentified";
          break;
      }
      return `${term} ${date.getUTCFullYear()}`;
    } catch (e) {
      throw Error("input is not in date format");
    }
  };

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
